import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Button, IconButton, Switch, Table, TableBody, TableCell, TableHead, TableRow, Tooltip
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import { Section, StatusBadge } from "../../components/ui";
import { usePanelStyles, SwitchRow } from "../../components/QueueIntegrationModal/panelKit";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import ErpWritebackRuleModal from "./ErpWritebackRuleModal";

const t = k => i18n.t(`erpWriteback.${k}`);
const ACTION_LABEL = { none: "actionNone", note: "actionNote", service_order: "actionOs" };
const TRIGGER_LABEL = { close: "triggerClose", transfer: "triggerTransfer", close_and_transfer: "triggerBoth", manual: "triggerManual" };
const STATUS_TONE = { ok: "success", error: "error", skipped: "neutral" };

const useStyles = makeStyles(theme => ({
  scroll: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    paddingBottom: 16,
    ...theme.scrollbarStyles
  },
  block: { flex: "none" },
  // a chave geral é um cartão da página, não uma linha de formulário
  enabledRow: { flex: "none", padding: "14px 20px", gap: 20 },
  tableScroll: {
    overflowX: "auto",
    ...theme.scrollbarStyles,
    "& .MuiTableRow-root:last-child > .MuiTableCell-body": { borderBottom: 0 }
  },
  nowrap: { whiteSpace: "nowrap" },
  errorCell: { maxWidth: 320, overflowWrap: "anywhere" }
}));

const ErpWriteback = () => {
  const classes = useStyles();
  const panel = usePanelStyles();
  const [enabled, setEnabled] = useState(false);
  const [rules, setRules] = useState([]);
  const [queues, setQueues] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [log, setLog] = useState([]);
  const [modal, setModal] = useState({ open: false, rule: null });
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r, l, q, i, s] = await Promise.all([
        api.get("/erp-writeback/rules"),
        api.get("/erp-writeback/log", { params: { limit: 50 } }),
        api.get("/queue"),
        api.get("/isp/erp-integrations"),
        api.get("/settings")
      ]);
      setRules(r.data || []);
      setLog(l.data || []);
      setQueues(q.data || []);
      setIntegrations((i.data || []).filter(x => ["SGP", "IXC"].includes(x.type)));
      const st = (s.data || []).find(x => x.key === "erpWritebackEnabled");
      setEnabled(st?.value === "enabled");
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async v => {
    setEnabled(v);
    try {
      await api.put("/settings/erpWritebackEnabled", { value: v ? "enabled" : "disabled" });
    } catch (err) {
      setEnabled(!v);
      toastError(err);
    }
  };

  const remove = async id => {
    try {
      await api.delete(`/erp-writeback/rules/${id}`);
      toast.success(t("deleted"));
      load();
    } catch (err) {
      toastError(err);
    }
  };

  const openNew = () => setModal({ open: true, rule: null });

  return (
    <MainContainer maxWidth>
      <MainHeader>
        <Title>{t("title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openNew}>{t("newRule")}</Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <div className={classes.scroll}>
        <SwitchRow className={classes.enabledRow} label={t("enabled")} help={t("enabledHint")}>
          <Switch
            checked={enabled}
            onChange={e => toggle(e.target.checked)}
            color="primary"
            inputProps={{ "aria-label": t("enabled") }}
          />
        </SwitchRow>

        <Section flush className={classes.block} title={`${t("rules")} (${rules.length})`}>
          <div className={classes.tableScroll}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("queue")}</TableCell>
                  <TableCell>{t("integration")}</TableCell>
                  <TableCell>{t("action")}</TableCell>
                  <TableCell>{t("trigger")}</TableCell>
                  <TableCell>{t("erpTypeId")}</TableCell>
                  <TableCell>{t("active")}</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className={panel.empty}>{t("noRules")}</TableCell>
                  </TableRow>
                )}
                {rules.map(r => (
                  <TableRow key={r.id}>
                    <TableCell className={panel.cellStrong}>{r.queue?.name || t("defaultRule")}</TableCell>
                    <TableCell>{r.erpIntegration ? `${r.erpIntegration.name} (${r.erpIntegration.type})` : r.erpIntegrationId}</TableCell>
                    <TableCell>{t(ACTION_LABEL[r.action] || "actionNone")}</TableCell>
                    <TableCell>{t(TRIGGER_LABEL[r.trigger] || "triggerClose")}</TableCell>
                    <TableCell>{r.erpTypeId || "-"}{r.erpSectorId ? ` / ${r.erpSectorId}` : ""}</TableCell>
                    <TableCell><StatusBadge status={r.enabled ? "enabled" : "disabled"} label={r.enabled ? "on" : "off"} /></TableCell>
                    <TableCell align="right">
                      <div className={panel.rowActions}>
                        <Tooltip title={t("edit")}>
                          <IconButton size="small" aria-label={t("edit")} onClick={() => setModal({ open: true, rule: r })}><EditIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title={t("remove")}>
                          <IconButton size="small" aria-label={t("remove")} onClick={() => setConfirm(r.id)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Section>

        <Section flush className={classes.block} title={t("log")}>
          <div className={classes.tableScroll}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("logWhen")}</TableCell>
                  <TableCell>{t("logTicket")}</TableCell>
                  <TableCell>{t("logAction")}</TableCell>
                  <TableCell>{t("logStatus")}</TableCell>
                  <TableCell>{t("logErp")}</TableCell>
                  <TableCell>{t("logError")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {log.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className={panel.empty}>{t("noLog")}</TableCell>
                  </TableRow>
                )}
                {log.map(w => (
                  <TableRow key={w.id}>
                    <TableCell className={classes.nowrap}>{new Date(w.createdAt).toLocaleString("pt-BR")}</TableCell>
                    <TableCell className={classes.nowrap}>#{w.ticketId}</TableCell>
                    <TableCell>{t(ACTION_LABEL[w.action] || "actionNone")} · {t(TRIGGER_LABEL[w.trigger] || "triggerManual")}</TableCell>
                    <TableCell>
                      <StatusBadge
                        color={STATUS_TONE[w.status] || "neutral"}
                        label={t(`status${w.status === "ok" ? "Ok" : w.status === "error" ? "Error" : "Skipped"}`)}
                      />
                    </TableCell>
                    <TableCell className={classes.nowrap}>{(w.provider || "").toUpperCase()} {w.erpId ? `#${w.erpId}` : ""}{w.erpProtocol ? ` (${w.erpProtocol})` : ""}</TableCell>
                    <TableCell className={`${panel.cellMuted} ${classes.errorCell}`}>{w.error || ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Section>
      </div>

      <ErpWritebackRuleModal open={modal.open} rule={modal.rule} queues={queues} integrations={integrations} onClose={() => setModal({ open: false, rule: null })} onSaved={load} />
      <ConfirmationModal title={t("confirmDelete")} open={!!confirm} onClose={() => setConfirm(null)} onConfirm={() => { remove(confirm); setConfirm(null); }}>
        {t("confirmDelete")}
      </ConfirmationModal>
    </MainContainer>
  );
};

export default ErpWriteback;
